import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import { FaPlus } from "react-icons/fa";
import moment from "moment-timezone";
import Button from "components/ui/Button";
import "../assets/css/style.css";
import {
  useCancelSocialPostMutation,
  useDeleteSocialPostMutation,
  useGetSocialPostsQuery,
} from "apis/socialPost";
import Modal from "components/ui/Modal";
import SocialPostDetails from "components/socialPost/SocialPostDetails";
import AddSocialPost from "components/socialPost/AddSocialPost";
import SocialSubscribers from "components/socialPost/SocialSubscribers";
import Table from "components/ui/Table";
import { FiEdit2, FiSlash, FiTrash2 } from "react-icons/fi";
import {
  canDeleteSocialPost,
  canCancelSocialPost,
  isVideoMedia,
  postMediaUrls,
} from "utils/socialPost";
import OrganizationFilter from "components/organization/OrganizationFilter";
import useDebounce from "hooks/useDebounce";

const SocialPost = () => {
  const pageSize = 20;
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");
  const [organizationId, setOrganizationId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pendingAction, setPendingAction] = useState(null);
  const search = useDebounce(searchInput, 300);

  const { mutateAsync: cancelPost } = useCancelSocialPostMutation();
  const { mutateAsync: deletePost } = useDeleteSocialPostMutation();

  const { data, pagination, isLoading, isFetching } = useGetSocialPostsQuery({
    page,
    limit: pageSize,
    ...(organizationId ? { organizationId } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });

  // A stale page number would show an empty table after narrowing the filters.
  const handleOrganizationChange = (nextOrganizationId) => {
    setOrganizationId(nextOrganizationId);
    setPage(1);
  };

  const handleSearchChange = (nextSearch) => {
    setSearchInput(nextSearch);
    setPage(1);
  };

  // Sorting is off across this table: the rows are one server page, so a sort
  // would silently reorder only what is on screen. The server returns newest
  // first; narrowing is done with the filters above.
  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "userId.name",
        header: "Agent Name",
        cell: (row) => (
          <div>
            <p>{row.userId?.name ?? "No Agent"}</p>
            {row.userId?.email && (
              <p className="text-mediumgray text-sm">{row.userId.email}</p>
            )}
            {row.userId?.instagramUsername && (
              <p className="text-mediumgray text-sm">
                @{row.userId.instagramUsername}
              </p>
            )}
          </div>
        ),
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "organization.name",
        header: "Organization",
        cell: (row) => <p>{row.organization?.name ?? "—"}</p>,
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "propertyId.address",
        header: "Property Address",
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "platform",
        header: "Platform",
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "type",
        header: "Post Type",
        cell: (row) => {
          if (row.postType) {
            switch (row.postType) {
              case "comingSoon": return <p>Coming Soon</p>;
              case "justListed": return <p>Just Listed</p>;
              case "soldPost": return <p>Sold Post</p>;
              case "soldStory": return <p>Sold Story</p>;
              case "memorableMoment": return <p>Memorable Moment</p>;
              default: return <p>{row.postType}</p>;
            }
          }
          if (row.type === "post") return <p>Just Listed Post</p>;
          if (row.type === "story") return <p>Coming Soon Story</p>;
          if (row.type === "reel") return <p>Just Listed Reel</p>;
          return <p>{row.type}</p>;
        },
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        isFilter: true,
        isSortable: false,
      },
      {
        accessorKey: "scheduleDate",
        header: "Scheduled Date",
        cell: (row) => (
          <p>
            {row.scheduleDate
              ? moment(row.scheduleDate)
                .tz("Australia/Sydney")
                .format("D MMM YYYY, h:mm A")
              : "N/A"}
          </p>
        ),
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "media",
        header: "Media",
        cell: (row) => {
          const [firstUrl] = postMediaUrls(row.media);
          if (!firstUrl) return <p>No Media</p>;
          return isVideoMedia(row, firstUrl) ? (
            <video
              src={encodeURI(firstUrl)}
              className="w-16 h-16 object-cover rounded"
            />
          ) : (
            <img
              src={firstUrl}
              alt="Post media"
              className="w-16 h-16 object-cover rounded"
            />
          );
        },
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => {
          const canCancel = canCancelSocialPost(row);
          const canDelete = canDeleteSocialPost(row);
          if (!canCancel && !canDelete) return <p className="text-mediumgray">—</p>;
          return (
            <div className="flex items-center space-x-4">
              {canCancel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewPost(row);
                  }}
                  className="edit-icon"
                  title="Edit post"
                >
                  <FiEdit2 fontSize={18} />
                </button>
              )}
              {canCancel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingAction({ post: row, action: "cancel" });
                  }}
                  className="edit-icon"
                  title="Stop this post going out"
                >
                  <FiSlash fontSize={18} />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingAction({ post: row, action: "delete" });
                  }}
                  className="delete-icon"
                  title="Delete post"
                >
                  <FiTrash2 fontSize={18} />
                </button>
              )}
            </div>
          );
        },
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "140px",
      },
    ],
    []
  );

  const handleCreatePost = () => {
    setIsAddOpen(true);
  };

  const handleViewPost = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const handleConfirmAction = async () => {
    const { post, action } = pendingAction;
    setPendingAction(null);
    if (action === "cancel") await cancelPost({ id: post._id });
    if (action === "delete") await deletePost({ id: post._id });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const AddPostButton = () => (
    <Button
      onClick={handleCreatePost}
      className="black-button flex items-center gap-2"
    >
      <FaPlus />
      Add Post
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        className="max-w-3xl"
      >
        <SocialPostDetails
          post={data?.find((post) => post._id === selectedId)}
          onDone={() => setIsViewOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        title={
          pendingAction?.action === "delete"
            ? "Delete this delivery?"
            : "Stop this delivery?"
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setPendingAction(null)}
              className="gray-button"
            >
              Keep it
            </Button>
            <Button onClick={handleConfirmAction} className="black-button">
              {pendingAction?.action === "delete" ? "Delete" : "Stop it"}
            </Button>
          </div>
        }
      >
        <p>
          {pendingAction?.action === "delete"
            ? "This removes one delivery from Beleef."
            : "This stops one delivery from going out."}{" "}
          {pendingAction?.post?.runDeliveries > 1
            ? `The other ${
                pendingAction.post.runDeliveries - 1
              } deliveries in this post are not touched — open the post to act on all of them.`
            : ""}
        </p>
      </Modal>

      <AddSocialPost
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />

      <div className="container mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">Social Posts</h1>
          <div className="flex items-center gap-2">
            {activeTab === "posts" && <AddPostButton />}
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-mediumgray">
          <button
            onClick={() => setActiveTab("posts")}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === "posts"
                ? "border-black text-black"
                : "border-transparent text-darkergray"
              }`}
          >
            Posts
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === "subscribers"
                ? "border-black text-black"
                : "border-transparent text-darkergray"
              }`}
          >
            Subscribers
          </button>
        </div>

        {activeTab === "subscribers" ? (
          <SocialSubscribers />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4 mb-4">
              <OrganizationFilter
                value={organizationId}
                onChange={handleOrganizationChange}
              />
              <div className="flex-1 min-w-64">
                <label className="block text-sm mb-1 font-medium">Search</label>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Agent, address, platform or status..."
                  className="form-input border border-mediumgray w-full"
                />
              </div>
            </div>

            <Table
              title={
                isFetching
                  ? "Loading…"
                  : `${pagination?.total ?? 0} post${
                      pagination?.total === 1 ? "" : "s"
                    }`
              }
              columns={columns}
              data={data || []}
              pageSize={pageSize}
              onRowClick={handleViewPost}
              searchable={false}
              serverPagination={
                pagination
                  ? {
                      page: pagination.page,
                      totalPages: pagination.totalPages,
                      total: pagination.total,
                      limit: pagination.limit,
                    }
                  : null
              }
              onPageChange={(nextPage) => setPage(nextPage)}
              ButtonCmp={() => null}
            />
          </>
        )}
      </div>
    </>
  );
};

export default SocialPost;
